import { useMemo } from 'react';

import { useAccessMode } from '../../context/AccessModeContext';
import { usePublicMemberDetail } from '../publicMember/hooks';
import type { SmartScheduleEvent } from '../smartSchedule/types';
import { getQuickMoveMemberLookupId, resolveQuickMoveEligibility } from './eligibility';
import { QuickMoveEligibility } from './types';

export interface UseQuickMoveEligibilityParams {
    centerId: string;
    event: SmartScheduleEvent | null | undefined;
    /** SchedulePage 의 smartGroupSessionsQuery.error 여부. */
    groupSourceFailed: boolean;
    /** 메뉴·시트가 실제로 보일 때만 true — 회원 상태 조회를 그때만 한다. */
    enabled?: boolean;
}

/**
 * 빠른 변경 가능 여부 훅. 열람 세션은 useAccessMode, 회원 상태는 상세 모달과 같은
 * usePublicMemberDetail 조회(같은 쿼리 키라 캐시 공유)로 판정한다.
 * 블록 메뉴마다 붙이면 회원마다 조회가 나가므로, 메뉴를 열었을 때만 enabled 로 켠다.
 */
export const useQuickMoveEligibility = ({
    centerId,
    event,
    groupSourceFailed,
    enabled = true,
}: UseQuickMoveEligibilityParams): QuickMoveEligibility => {
    const { isReadOnly } = useAccessMode();
    const lookupMemberId = getQuickMoveMemberLookupId(event);
    const memberQuery = usePublicMemberDetail({
        centerId: centerId || '',
        memberId: lookupMemberId,
        enabled: enabled && !isReadOnly && !!lookupMemberId,
    });

    const memberAccount = lookupMemberId
        ? {
              // 비활성(enabled=false) 상태에서도 v4 isLoading 은 true 다 → '확인 중'으로 남는다(fail-closed).
              isLoading: memberQuery.isLoading,
              isError: memberQuery.isError,
              centerMember: memberQuery.data?.centerMember,
          }
        : null;

    return useMemo(
        () => resolveQuickMoveEligibility({ event, isReadOnly, groupSourceFailed, memberAccount }),
        // memberAccount 는 매 렌더 새 객체라 구성 값으로 의존한다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [event, isReadOnly, groupSourceFailed, memberAccount?.isLoading, memberAccount?.isError, memberAccount?.centerMember]
    );
};
